/**
 * downloadParticipantReport
 *
 * Calls the backend Excel-generation API and triggers a browser download.
 *
 * Endpoint (implemented on BE, EP-12):
 *   POST /api/v1/events/{eventId}/reports/{reportType}?lang={lang}
 *   Response: application/vnd.openxmlformats-officedocument.spreadsheetml.sheet
 *
 * SHA3 hashing / S3 caching is handled entirely by the backend — the frontend has no
 * awareness of cache keys.
 */

import { getApiToken } from '@proaxiv/server-api'
import type { ReportType } from './reportTypes'

/** Language codes accepted by the printable-reports PDF endpoint. */
export type ReportLanguage = 'en' | 'ja'

/**
 * Normalise an i18next language code (e.g. "ja-JP", "en-US") to the two values the
 * backend accepts.
 *
 * Matches any code merely containing "ja" or "jp" (case-insensitively) as Japanese — not just an
 * exact/prefix "ja" — so region-qualified browser locales (e.g. "ja-JP", "ja_JP", a bare region
 * code like "JP") are never misdetected as unsupported and silently defaulted to English.
 */
export function normaliseLanguage(i18nLang: string): ReportLanguage {
  const lower = i18nLang.toLowerCase()
  return lower.includes('ja') || lower.includes('jp') ? 'ja' : 'en'
}

/**
 * Triggers a browser download of the participant list Excel spreadsheet for the given event
 * and report type.  Language is derived from the active UI locale.
 *
 * @throws  Error with a human-readable message on non-2xx responses.
 */
export async function downloadParticipantReport(
  eventId: string,
  reportType: ReportType,
  language: ReportLanguage
): Promise<void> {
  const token = getApiToken()
  const headers: HeadersInit = token ? { Authorization: `Bearer ${token}` } : {}
  const res = await fetch(`/api/v1/events/${eventId}/reports/${reportType}?lang=${language}`, {
    method: 'POST',
    headers,
  })
  if (!res.ok) {
    throw new Error(`Report generation failed (HTTP ${res.status})`)
  }
  const blob = await res.blob()
  triggerDownload(blob, buildFileName(reportType, language))
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function buildFileName(reportType: ReportType, language: ReportLanguage): string {
  return `report_${reportType}_${language}.xlsx`
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
